import { useState } from "react";
import { Layout } from "@/components/Layout";
import { useMyLoads } from "@/hooks/useMyLoads";
import { usePickups } from "@/hooks/usePickups";
import { usePickupNotifications } from "@/hooks/usePickupNotifications";
import { useMyLoadsMutations } from "@/hooks/useMyLoadsMutations";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { MyLoadsListView } from "@/components/loads/MyLoadsListView";
import { MyLoadDetailView } from "@/components/loads/MyLoadDetailView";
import { ConflictDialog } from "@/components/ui/conflict-dialog";
import { FailedActionsDialog } from "@/components/ui/failed-actions-dialog";

export default function MyLoads() {
  const { loads, isLoading, installationsMap } = useMyLoads();
  const { unassignedPickups, claimedPickups, claimPickup, isClaimingPickup } = usePickups();
  const {
    updateLoadStatus,
    updateInstallationStatus,
    handleSendSms,
    handlePickupCompleted,
    sendingSmsFor,
    notifiedInstallations,
    isUpdatePending,
  } = useMyLoadsMutations();
  
  // Offline sync state
  const {
    currentConflict,
    currentConflictAction,
    failedActions,
    resolveConflict,
    retryAction,
    discardAction,
  } = useOfflineSync();
  
  // Enable realtime notifications for pickups
  usePickupNotifications();
  
  // Core navigation state
  const [selectedLoadId, setSelectedLoadId] = useState<string | null>(null);
  const [showFailedDialog, setShowFailedDialog] = useState(false);

  // Render detail view when a load is selected
  if (selectedLoadId) {
    const load = loads?.find(l => l.id === selectedLoadId);
    const installations = installationsMap?.[selectedLoadId] || [];
    
    return (
      <Layout>
        <MyLoadDetailView
          load={load}
          installations={installations}
          onBack={() => setSelectedLoadId(null)}
          onUpdateLoadStatus={updateLoadStatus}
          onUpdateInstallationStatus={updateInstallationStatus}
          onSendSms={handleSendSms}
          sendingSmsFor={sendingSmsFor}
          notifiedInstallations={notifiedInstallations}
          isUpdatePending={isUpdatePending}
        />
      </Layout>
    );
  }

  // Render list view
  return (
    <Layout>
      {/* Conflict resolution dialog */}
      <ConflictDialog
        conflict={currentConflict}
        action={currentConflictAction || undefined}
        onOverwrite={() => resolveConflict('overwrite')}
        onDiscard={() => resolveConflict('discard')}
      />
      
      {/* Failed actions dialog */}
      <FailedActionsDialog
        open={showFailedDialog}
        onOpenChange={setShowFailedDialog}
        failedActions={failedActions}
        onRetry={retryAction}
        onDiscard={discardAction}
      />
      
      <MyLoadsListView
        loads={loads || []}
        installationsMap={installationsMap || {}}
        unassignedPickups={unassignedPickups}
        claimedPickups={claimedPickups}
        isLoading={isLoading}
        onSelectLoad={setSelectedLoadId}
        onClaimPickup={claimPickup}
        onCompletePickup={handlePickupCompleted}
        onSendSms={handleSendSms}
        isClaimingPickup={isClaimingPickup}
        sendingSmsFor={sendingSmsFor}
        notifiedInstallations={notifiedInstallations}
        isCompleteLoading={isUpdatePending}
        onShowFailedActions={() => setShowFailedDialog(true)}
        failedActionsCount={failedActions.length}
      />
    </Layout>
  );
}
